import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../utils/axios";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";
import { useSocket } from "../contexts/SocketContext";
import {
  Clock,
  XCircle,
  Search,
  Eye,
  Package,
  DollarSign,
  Bell,
  BellOff,
  Receipt,
  Printer,
  Download,
  Share2,
  Smartphone,
  Car,
} from "lucide-react";
import OrderModal from "../components/OrderModal";

// Helper function to get display order ID (order_group_id if available, otherwise order.id)
const getDisplayOrderId = (order) => {
  return order.order_group_id || order.id;
};

const Orders = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [orderTypeFilter, setOrderTypeFilter] = useState("");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("");
  const [dateRangeFilter, setDateRangeFilter] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showKotView, setShowKotView] = useState(false);
  const [kotOrder, setKotOrder] = useState(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState(() => {
    const saved = localStorage.getItem("notificationSettings");
    return saved
      ? JSON.parse(saved)
      : {
          sound: true,
          browser: true,
          toast: true,
        };
  });
  const [loadingStates, setLoadingStates] = useState({});
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { socket } = useSocket();

  const {
    data: orders,
    isLoading,
    error,
  } = useQuery(
    [
      "orders",
      statusFilter,
      orderTypeFilter,
      paymentMethodFilter,
      dateRangeFilter,
    ],
    async () => {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (orderTypeFilter) params.order_type = orderTypeFilter;
      if (paymentMethodFilter) params.payment_method = paymentMethodFilter;
      if (dateRangeFilter) params.date_range = dateRangeFilter;

      const response = await api.get("/vendor/orders", { params });
      return response.data;
    },
    {
      refetchInterval: 30000, // Refresh every 30 seconds
      retry: 3,
      retryDelay: 1000,
      onError: (error) => {
        console.error("Orders query error:", error);
      },
    }
  );

  const updateOrderStatusMutation = useMutation(
    async ({ orderId, status }) => {
      const response = await api.post(`/vendor/orders/${orderId}/status`, {
        status,
      });
      return response.data;
    },
    {
      onMutate: ({ orderId }) => {
        setLoadingStates((prev) => ({ ...prev, [orderId]: true }));
      },
      onSuccess: () => {
        queryClient.invalidateQueries(["orders"]);
        showSuccessToast("Order status updated successfully!", "order-status");
        setSelectedOrder(null);
      },
      onError: (error) => {
        const errorMessage =
          error.response?.data?.message || "Failed to update order status";
        showErrorToast(errorMessage, "order-status");
        console.error(
          "Order status update error:",
          error.response?.data || error.message
        );
      },
      onSettled: (_, __, { orderId }) => {
        setLoadingStates((prev) => ({ ...prev, [orderId]: false }));
      },
    }
  );

  const approveCashOrderMutation = useMutation(
    async (orderId) => {
      const response = await api.put(`/vendor/orders/${orderId}/approve-cash`);
      return response.data;
    },
    {
      onMutate: (orderId) => {
        setLoadingStates((prev) => ({ ...prev, [orderId]: true }));
      },
      onSuccess: () => {
        queryClient.invalidateQueries(["orders"]);
        showSuccessToast("Cash order approved successfully!", "cash-approval");
        setSelectedOrder(null);
      },
      onError: (error) => {
        showErrorToast(
          error.response?.data?.message || "Failed to approve cash order",
          "cash-approval"
        );
      },
      onSettled: (_, __, orderId) => {
        setLoadingStates((prev) => ({ ...prev, [orderId]: false }));
      },
    }
  );

  // New mutation for generating KOT
  const generateKotMutation = useMutation(
    async (orderId) => {
      const response = await api.post(`/vendor/orders/${orderId}/generate-kot`);
      return response.data;
    },
    {
      onMutate: (orderId) => {
        setLoadingStates((prev) => ({ ...prev, [`kot-${orderId}`]: true }));
      },
      onSuccess: (data, orderId) => {
        queryClient.invalidateQueries(["orders"]);
        showSuccessToast(
          `KOT generated successfully! Reference: ${data.kot_reference}`,
          "kot-generation"
        );
        setSelectedOrder(null);

        // Find the order and open KOT modal
        if (orders) {
          const order = orders.find(
            (o) => o.id.toString() === orderId.toString()
          );
          if (order) {
            const updatedOrder = {
              ...order,
              kot_reference: data.kot_reference,
            };
            setKotOrder(updatedOrder);
            setShowKotView(true);
          }
        }
      },
      onError: (error) => {
        showErrorToast(
          error.response?.data?.message || "Failed to generate KOT",
          "kot-generation"
        );
      },
      onSettled: (_, __, orderId) => {
        setLoadingStates((prev) => ({ ...prev, [`kot-${orderId}`]: false }));
      },
    }
  );

  const filteredOrders =
    orders
      ?.filter(
        (order) =>
          order.id.toString().includes(searchTerm) ||
          order.user_id.toString().includes(searchTerm) ||
          order.order_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.payment_method
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (order.orderItems &&
            order.orderItems.some((item) =>
              item.menuItem?.name
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase())
            ))
      )
      .sort((a, b) => {
        let aValue, bValue;

        switch (sortBy) {
          case "date":
            aValue = new Date(a.created_at).getTime();
            bValue = new Date(b.created_at).getTime();
            break;
          case "total":
            aValue = parseFloat(a.subtotal || 0);
            bValue = parseFloat(b.subtotal || 0);
            break;
          case "id":
            aValue = parseInt(a.id);
            bValue = parseInt(b.id);
            break;
          default:
            aValue = new Date(a.created_at).getTime();
            bValue = new Date(b.created_at).getTime();
        }

        return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
      }) || [];

  // Listen for real-time order updates
  useEffect(() => {
    if (!socket) return;

    const handleOrderNotification = (notification) => {
      console.log(
        "📦 Received order notification in Orders page:",
        notification
      );

      // Refresh the orders data when we receive notifications
      queryClient.invalidateQueries(["orders"]);
    };

    // Subscribe to order notifications
    socket.on("order-notification", handleOrderNotification);

    return () => {
      socket.off("order-notification", handleOrderNotification);
    };
  }, [socket, queryClient]);

  // Handle URL parameters for direct order access
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const orderId = searchParams.get("order");
    const action = searchParams.get("action");

    if (orderId && orders) {
      const order = orders.find((o) => o.id.toString() === orderId);
      if (order) {
        setSelectedOrder(order);

        // Show action-specific toast message
        if (action === "view") {
          showSuccessToast(`Order #${orderId} details opened`, "order-view");
        } else if (action === "status") {
          showSuccessToast(
            `Order #${orderId} status updated successfully`,
            "order-status"
          );
        }

        // Clear URL parameters after handling
        navigate("/orders", { replace: true });
      } else {
        showErrorToast(`Order #${orderId} not found`, "order-not-found");
        navigate("/orders", { replace: true });
      }
    }
  }, [orders, location.search, navigate]);

  // Enhanced status update with better feedback
  const handleStatusUpdate = async (orderId, status) => {
    try {
      await updateOrderStatusMutation.mutateAsync({ orderId, status });

      // Show enhanced success message
      showSuccessToast(
        `Order #${orderId} status changed to ${status}`,
        "order-status-update"
      );
    } catch (error) {
      // Enhanced error message
      showErrorToast(
        error.response?.data?.message || "Failed to update order status",
        "order-status-update"
      );
    }
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setOrderTypeFilter("");
    setPaymentMethodFilter("");
    setDateRangeFilter("");
    setSortBy("date");
    setSortOrder("desc");
  };

  const updateNotificationSettings = (newSettings) => {
    const updatedSettings = { ...notificationSettings, ...newSettings };
    setNotificationSettings(updatedSettings);
    localStorage.setItem(
      "notificationSettings",
      JSON.stringify(updatedSettings)
    );
  };

  const hasActiveFilters =
    searchTerm ||
    statusFilter ||
    orderTypeFilter ||
    paymentMethodFilter ||
    dateRangeFilter;

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "pending_approval":
        return "bg-orange-100 text-orange-800";
      case "confirmed":
        return "bg-blue-100 text-blue-800";
      case "preparing":
        return "bg-purple-100 text-purple-800";
      case "ready":
        return "bg-green-100 text-green-800";
      case "delayed":
        return "bg-red-100 text-red-800";
      case "at_pickup_point":
        return "bg-cyan-100 text-cyan-800";
      case "out_for_delivery":
        return "bg-indigo-100 text-indigo-800";
      case "delivered":
        return "bg-green-100 text-green-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getNextStatus = (order) => {
    const currentStatus = order.status;
    const orderType = order.order_type;
    const isDelivery = orderType === "delivery";
    const isClickAndCollect = orderType === "click_and_collect";
    const deliveryPremisesType = order.delivery_premises_type || null;
    const pickupBoothEnabled = order.pickup_booth_enabled || false;
    const paymentMethod = order.payment_method;

    // Special handling for cash at counter orders
    if (
      paymentMethod === "cash_at_counter" &&
      currentStatus === "pending_approval"
    ) {
      return "confirmed";
    }

    // Handle delayed status - can recover to preparing or ready
    if (currentStatus === "delayed") {
      return "preparing"; // Default recovery path
    }

    // Base transitions (removed 'packed', kept 'delayed')
    if (currentStatus === "pending" || currentStatus === "pending_approval") {
      return "confirmed";
    }
    if (currentStatus === "confirmed") {
      return "preparing";
    }
    if (currentStatus === "preparing") {
      return "ready";
    }

    // Status transitions from 'ready' based on order type and configuration
    if (currentStatus === "ready") {
      if (isClickAndCollect) {
        // Click & Collect orders
        if (pickupBoothEnabled) {
          return "at_pickup_point";
        } else {
          return "completed";
        }
      } else if (isDelivery) {
        // Delivery orders
        const isInsidePremises = deliveryPremisesType === "inside";

        if (isInsidePremises) {
          // Inside Premises Delivery
          if (pickupBoothEnabled) {
            return "at_pickup_point";
          } else {
            // Vendor stops at ready, driver handles the rest
            return null;
          }
        } else {
          // Outside Premises Delivery (backward compatibility)
          if (pickupBoothEnabled) {
            return "at_pickup_point";
          } else {
            return "out_for_delivery";
          }
        }
      } else {
        // Other order types (dine_in, takeaway)
        return "completed";
      }
    }

    // Handle at_pickup_point status
    if (currentStatus === "at_pickup_point") {
      if (isClickAndCollect) {
        return "completed";
      } else if (isDelivery) {
        const isInsidePremises = deliveryPremisesType === "inside";
        if (isInsidePremises) {
          // Driver handles out_for_delivery and delivered
          return null;
        } else {
          // Outside premises - vendor can mark as out_for_delivery
          return "out_for_delivery";
        }
      }
    }

    return null;
  };

  // Add this function after getNextStatus (around line 419)
  const formatStatusLabel = (status) => {
    const statusLabels = {
      pending: "Pending",
      pending_approval: "Pending Approval",
      confirmed: "Confirmed",
      preparing: "Preparing",
      ready: "Ready",
      delayed: "Delayed",
      at_pickup_point: "At Pickup Point",
      out_for_delivery: "Out for Delivery",
      delivered: "Delivered",
      completed: "Completed",
      cancelled: "Cancelled",
    };
    return (
      statusLabels[status] || status.charAt(0).toUpperCase() + status.slice(1)
    );
  };

  const getStatusFlow = (order) => {
    const isDelivery = order.order_type === "delivery";
    const isClickAndCollect = order.order_type === "click_and_collect";
    const deliveryPremisesType = order.delivery_premises_type;
    const pickupBoothEnabled = order.pickup_booth_enabled || false;

    // Base status flow (common for all order types)
    const baseFlow = [
      {
        status: "pending",
        label: "Pending",
        color: "bg-yellow-100 text-yellow-800",
        icon: "⏳",
      },
      {
        status: "pending_approval",
        label: "Pending Approval",
        color: "bg-orange-100 text-orange-800",
        icon: "🔍",
      },
      {
        status: "confirmed",
        label: "Confirmed",
        color: "bg-blue-100 text-blue-800",
        icon: "✅",
      },
      {
        status: "preparing",
        label: "Preparing",
        color: "bg-purple-100 text-purple-800",
        icon: "👨‍🍳",
      },
      {
        status: "ready",
        label: "Ready",
        color: "bg-green-100 text-green-800",
        icon: "🎉",
      },
    ];

    // Delayed status (can occur at any point during preparation)
    const delayedStatus = {
      status: "delayed",
      label: "Delayed",
      color: "bg-red-100 text-red-800",
      icon: "⏰",
    };

    let statusFlow = [...baseFlow];

    // Add delayed status to flow (for display purposes - it's not a linear step)
    statusFlow.push(delayedStatus);

    if (isClickAndCollect) {
      // Click & Collect orders
      if (pickupBoothEnabled) {
        // Click & Collect + Pickup Booth Enabled: add at_pickup_point before completed
        statusFlow.push({
          status: "at_pickup_point",
          label: "At Pickup Point",
          color: "bg-cyan-100 text-cyan-800",
          icon: "📍",
        });
      }
      statusFlow.push({
        status: "completed",
        label: "Completed",
        color: "bg-green-100 text-green-800",
        icon: "✅",
      });
    } else if (isDelivery) {
      // Delivery orders
      const isInsidePremises = deliveryPremisesType === "inside";

      if (isInsidePremises) {
        // Inside Premises Delivery
        if (pickupBoothEnabled) {
          // Inside Premises + Pickup Booth Enabled: add at_pickup_point
          statusFlow.push({
            status: "at_pickup_point",
            label: "At Pickup Point",
            color: "bg-cyan-100 text-cyan-800",
            icon: "📍",
          });
        }
        // For inside premises, vendor stops here - driver handles out_for_delivery and delivered
        // Don't add out_for_delivery or delivered to vendor flow
      } else {
        // Outside Premises Delivery
        if (pickupBoothEnabled) {
          statusFlow.push({
            status: "at_pickup_point",
            label: "At Pickup Point",
            color: "bg-cyan-100 text-cyan-800",
            icon: "📍",
          });
        }
        statusFlow.push({
          status: "out_for_delivery",
          label: "Out for Delivery",
          color: "bg-indigo-100 text-indigo-800",
          icon: "🚚",
        });
        statusFlow.push({
          status: "delivered",
          label: "Delivered",
          color: "bg-green-100 text-green-800",
          icon: "✅",
        });
      }
    } else {
      // Other order types (dine_in, takeaway)
      statusFlow.push({
        status: "completed",
        label: "Completed",
        color: "bg-green-100 text-green-800",
        icon: "✅",
      });
    }

    // Always add cancelled at the end
    statusFlow.push({
      status: "cancelled",
      label: "Cancelled",
      color: "bg-red-100 text-red-800",
      icon: "❌",
    });

    const currentIndex = statusFlow.findIndex((s) => s.status === order.status);
    const nextStatus = getNextStatus(order);
    const nextIndex = nextStatus
      ? statusFlow.findIndex((s) => s.status === nextStatus)
      : -1;

    return {
      flow: statusFlow,
      currentIndex,
      nextIndex,
      nextStatus,
      canProgress: nextIndex > currentIndex,
      canCancel:
        order.status === "pending" || order.status === "pending_approval",
    };
  };

  const getStatusAction = (order) => {
    const statusFlow = getStatusFlow(order);

    if (!statusFlow.nextStatus) return null;

    if (
      order.status === "pending_approval" &&
      order.payment_method === "cash_at_counter"
    ) {
      return {
        label: "Approve Cash Order",
        action: () => approveCashOrderMutation.mutate(order.id),
        loading: approveCashOrderMutation.isLoading,
        color: "bg-green-600 hover:bg-green-700",
        icon: "💵",
      };
    }

    return {
      label: `Mark as ${formatStatusLabel(statusFlow.nextStatus)}`,
      action: () => handleStatusUpdate(order.id, statusFlow.nextStatus),
      loading: updateOrderStatusMutation.isLoading,
      color: "bg-primary-600 hover:bg-primary-700",
      icon: "➡️",
    };
  };

  // New function for inline status changes
  const getInlineStatusActions = (order) => {
    const statusFlow = getStatusFlow(order);
    const actions = [];
    const isOrderLoading = loadingStates[order.id] || false;

    // Add next status action
    if (statusFlow.nextStatus) {
      if (
        order.status === "pending_approval" &&
        order.payment_method === "cash_at_counter"
      ) {
        actions.push({
          label: "Approve",
          action: () => approveCashOrderMutation.mutate(order.id),
          loading: isOrderLoading,
          color: "bg-green-600 hover:bg-green-700",
          icon: "💵",
          size: "sm",
        });
      } else {
        actions.push({
          label: formatStatusLabel(statusFlow.nextStatus),
          action: () => handleStatusUpdate(order.id, statusFlow.nextStatus),
          loading: isOrderLoading,
          color: "bg-primary-600 hover:bg-primary-700",
          icon: "➡️",
          size: "sm",
        });
      }
    }

    // Special case: Show disabled "Delivered" button for delivery orders that are "out_for_delivery"
    if (
      order.order_type === "delivery" &&
      order.status === "out_for_delivery"
    ) {
      actions.push({
        label: "Delivered",
        action: null,
        loading: false,
        color: "bg-gray-400 cursor-not-allowed opacity-50",
        icon: "✅",
        size: "sm",
        disabled: true,
      });
    }

    // Add cancel action for early stages
    if (statusFlow.canCancel) {
      actions.push({
        label: "Cancel",
        action: () => {
          if (
            window.confirm(
              `Are you sure you want to cancel order #${order.id}?`
            )
          ) {
            updateOrderStatusMutation.mutate({
              orderId: order.id,
              status: "cancelled",
            });
          }
        },
        loading: isOrderLoading,
        color: "bg-red-600 hover:bg-red-700",
        icon: "❌",
        size: "sm",
      });
    }

    return actions;
  };

  // New function for KOT actions
  const getKotActions = (order) => {
    const actions = [];
    const isKotLoading = loadingStates[`kot-${order.id}`] || false;

    // Check for KOT reference in multiple possible locations
    const kotReference =
      order.kot_reference || order.kotReference || order.kot?.reference;

    if (kotReference) {
      // View KOT action
      actions.push({
        label: "View KOT",
        action: () => {
          console.log(
            "Opening KOT for order:",
            order.id,
            "KOT ref:",
            kotReference
          );
          setKotOrder(order);
          setShowKotView(true);
        },
        color: "bg-green-600 hover:bg-green-700",
        icon: "📋",
        size: "sm",
      });
    } else {
      // Generate KOT action - now opens KOT modal after generation
      actions.push({
        label: "Generate KOT",
        action: () => {
          if (window.confirm(`Generate KOT for order #${order.id}?`)) {
            generateKotMutation.mutate(order.id);
          }
        },
        loading: isKotLoading,
        color: "bg-blue-600 hover:bg-blue-700",
        icon: "🖨️",
        size: "sm",
      });
    }

    return actions;
  };

  const KotModal = ({ order, onClose }) => {
    const handlePrint = () => {
      const printWindow = window.open("", "_blank");
      const kotContent = `
        <html>
          <head>
            <title>KOT ${
              order.kot_reference || order.kotReference || order.kot?.reference
            }</title>
            <style>
              @page {
                size: A4;
                margin: 10mm;
              }
              body { 
                font-family: monospace; 
                margin: 0;
                padding: 10mm;
                font-size: 12px;
              }
              .header { 
                text-align: center; 
                border-bottom: 2px solid #000; 
                padding-bottom: 10px; 
                margin-bottom: 15px; 
              }
              .header h1 {
                margin: 0 0 10px 0;
                font-size: 18px;
              }
              .item { 
                margin: 8px 0; 
                padding: 5px 0; 
                page-break-inside: avoid;
              }
              .addon { 
                margin-left: 20px; 
                font-size: 11px; 
              }
              .total { 
                font-weight: bold; 
                font-size: 14px; 
                text-align: center; 
                margin-top: 15px; 
                page-break-inside: avoid;
              }
              .footer { 
                text-align: center; 
                margin-top: 15px; 
                font-size: 10px; 
              }
              @media print { 
                body { 
                  margin: 0;
                  padding: 10mm;
                }
                .item {
                  page-break-inside: avoid;
                }
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>KITCHEN ORDER TICKET</h1>
              <p><strong>KOT:</strong> ${
                order.kot_reference ||
                order.kotReference ||
                order.kot?.reference
              } | <strong>Order:</strong> #${getDisplayOrderId(order)}</p>
              <p>${new Date(order.created_at).toLocaleString()}</p>
            </div>
            
            <div class="item">
              <strong>Customer:</strong> ${
                order.user_id
              } | <strong>Type:</strong> ${order.order_type.toUpperCase()}
            </div>
            
            <h3>ORDER ITEMS:</h3>
            ${
              order.orderItems
                ?.map(
                  (item) => `
              <div class="item">
                <strong>${item.quantity}x</strong> ${
                    item.menuItem?.name || `Item #${item.menu_item_id}`
                  } - £${item.price}
                ${
                  item.addons && item.addons.length > 0
                    ? `
                  ${item.addons
                    .map(
                      (addon) => `
                    <div class="addon">+ ${addon.quantity}x ${
                        addon.menuItemAddon?.name ||
                        `Addon #${addon.menu_item_addon_id}`
                      } - £${addon.price_at_time}</div>
                  `
                    )
                    .join("")}
                `
                    : ""
                }
              </div>
            `
                )
                .join("") || "No items"
            }
            
            ${
              order.delivery_instructions
                ? `
              <div class="item">
                <strong>Special Instructions:</strong> ${order.delivery_instructions}
              </div>
            `
                : ""
            }
            
            ${
              order.allergy_note
                ? `
              <div class="item">
                <strong>⚠️ Allergy Info:</strong> ${order.allergy_note}
              </div>
            `
                : ""
            }
            
            <div class="total">
              <strong>TOTAL: £${order.subtotal || 0}</strong><br>
              Payment: ${
                order.payment_method === "cash_at_counter"
                  ? "Cash at Counter"
                  : "Card Payment"
              }
            </div>
            
            <div class="footer">
              Generated: ${new Date().toLocaleString()}
            </div>
          </body>
        </html>
      `;

      printWindow.document.write(kotContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    };

    const handleDownload = () => {
      // Create a downloadable text version
      const kotContent = `
KITCHEN ORDER TICKET
====================

KOT Reference: ${
        order.kot_reference || order.kotReference || order.kot?.reference
      }
Order ID: #${getDisplayOrderId(order)}
Date: ${new Date(order.created_at).toLocaleString()}

CUSTOMER DETAILS
----------------
Customer ID: ${order.user_id}
Order Type: ${order.order_type.toUpperCase()}

ORDER ITEMS
-----------
${
  order.orderItems
    ?.map((item) => {
      let itemText = `${item.quantity}x ${
        item.menuItem?.name || `Item #${item.menu_item_id}`
      } - £${item.price}`;
      if (item.addons && item.addons.length > 0) {
        itemText += "\n  Add-ons:";
        item.addons.forEach((addon) => {
          itemText += `\n    ${addon.quantity}x ${
            addon.menuItemAddon?.name || `Addon #${addon.menu_item_addon_id}`
          } - £${addon.price_at_time}`;
        });
      }
      return itemText;
    })
    .join("\n") || "No items"
}

SPECIAL INSTRUCTIONS
--------------------
${order.delivery_instructions || "None"}

ALLERGY INFORMATION
-------------------
${order.allergy_note || "None"}

TOTAL: £${order.subtotal || 0}
Payment: ${
        order.payment_method === "cash_at_counter"
          ? "Cash at Counter"
          : "Card Payment"
      }
Status: ${formatStatusLabel(order.status)}

Generated: ${new Date().toLocaleString()}
      `.trim();

      const blob = new Blob([kotContent], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `KOT_${
        order.kot_reference ||
        order.kotReference ||
        order.kot?.reference ||
        order.id
      }.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    };

    const handleShare = async () => {
      const kotText = `KOT: ${
        order.kot_reference || order.kotReference || order.kot?.reference
      }\nOrder #${getDisplayOrderId(order)}\nTotal: £${
        order.subtotal || 0
      }\nStatus: ${formatStatusLabel(order.status)}`;

      if (navigator.share) {
        try {
          await navigator.share({
            title: `KOT ${
              order.kot_reference || order.kotReference || order.kot?.reference
            }`,
            text: kotText,
          });
        } catch (err) {
          console.log("Error sharing:", err);
        }
      } else {
        // Fallback: copy to clipboard
        navigator.clipboard.writeText(kotText).then(() => {
          showSuccessToast("KOT details copied to clipboard!", "kot-share");
        });
      }
    };

    const handleQuickPrint = () => {
      // Auto-print without opening print dialog
      const printWindow = window.open("", "_blank");
      const kotContent = `
        <html>
          <head>
            <title>KOT ${
              order.kot_reference || order.kotReference || order.kot?.reference
            }</title>
            <style>
              body { font-family: monospace; margin: 20px; }
              .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 20px; }
              .item { margin: 10px 0; padding: 5px 0; }
              .addon { margin-left: 20px; font-size: 0.9em; }
              .total { font-weight: bold; font-size: 1.2em; text-align: center; margin-top: 20px; }
              .footer { text-align: center; margin-top: 20px; font-size: 0.8em; }
              @media print { body { margin: 0; } }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>KITCHEN ORDER TICKET</h1>
              <p><strong>KOT:</strong> ${
                order.kot_reference ||
                order.kotReference ||
                order.kot?.reference
              } | <strong>Order:</strong> #${getDisplayOrderId(order)}</p>
              <p>${new Date(order.created_at).toLocaleString()}</p>
            </div>
            
            <div class="item">
              <strong>Customer:</strong> ${
                order.user_id
              } | <strong>Type:</strong> ${order.order_type.toUpperCase()}
            </div>
            
            <h3>ORDER ITEMS:</h3>
            ${
              order.orderItems
                ?.map(
                  (item) => `
              <div class="item">
                <strong>${item.quantity}x</strong> ${
                    item.menuItem?.name || `Item #${item.menu_item_id}`
                  } - £${item.price}
                ${
                  item.addons && item.addons.length > 0
                    ? `
                  ${item.addons
                    .map(
                      (addon) => `
                    <div class="addon">+ ${addon.quantity}x ${
                        addon.menuItemAddon?.name ||
                        `Addon #${addon.menu_item_addon_id}`
                      } - £${addon.price_at_time}</div>
                  `
                    )
                    .join("")}
                `
                    : ""
                }
              </div>
            `
                )
                .join("") || "No items"
            }
            
            ${
              order.delivery_instructions
                ? `
              <div class="item">
                <strong>Special Instructions:</strong> ${order.delivery_instructions}
              </div>
            `
                : ""
            }
            
            ${
              order.allergy_note
                ? `
              <div class="item">
                <strong>⚠️ Allergy Info:</strong> ${order.allergy_note}
              </div>
            `
                : ""
            }
            
            <div class="total">
              <strong>TOTAL: £${order.subtotal || 0}</strong><br>
              Payment: ${
                order.payment_method === "cash_at_counter"
                  ? "Cash at Counter"
                  : "Card Payment"
              }
            </div>
            
            <div class="footer">
              Generated: ${new Date().toLocaleString()}
            </div>
          </body>
        </html>
      `;

      printWindow.document.write(kotContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-6 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Receipt className="h-8 w-8" />
                <div>
                  <h2 className="text-xl font-bold">Kitchen Order Ticket</h2>
                  <p className="text-green-100">
                    KOT Reference:{" "}
                    {order.kot_reference ||
                      order.kotReference ||
                      order.kot?.reference}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleQuickPrint}
                  className="p-2 bg-green-500 hover:bg-green-400 rounded-lg transition-colors"
                  title="Quick Print"
                >
                  <Printer className="h-5 w-5" />
                </button>
                <button
                  onClick={onClose}
                  className="text-green-100 hover:text-white p-1 rounded"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>

          {/* KOT Content */}
          <div className="p-6" id="kot-content">
            {/* Order Header */}
            <div className="border-b border-secondary-200 pb-4 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="font-bold text-lg text-secondary-900">
                    Order #{getDisplayOrderId(order)}
                  </h3>
                  <p className="text-secondary-600">
                    <Clock className="inline h-4 w-4 mr-1" />
                    {new Date(order.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="text-left md:text-right">
                  <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
                    <span className="mr-1">📋</span>
                    {order.kot_reference ||
                      order.kotReference ||
                      order.kot?.reference}
                  </div>
                  <p className="text-sm text-secondary-600 mt-1">
                    Customer ID: {order.user_id}
                  </p>
                </div>
              </div>
            </div>

            {/* Order Type & Payment */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-secondary-50 p-4 rounded-lg border border-secondary-200">
                <h4 className="font-semibold text-secondary-900 mb-2">
                  Order Type
                </h4>
                <div className="flex items-center space-x-2">
                  <Package className="h-5 w-5 text-secondary-600" />
                  <span className="font-medium text-lg capitalize">
                    {order.order_type.replace("_", " ")}
                  </span>
                </div>
              </div>
              <div className="bg-secondary-50 p-4 rounded-lg border border-secondary-200">
                <h4 className="font-semibold text-secondary-900 mb-2">
                  Payment Method
                </h4>
                <div className="flex items-center space-x-2">
                  <DollarSign className="h-5 w-5 text-secondary-600" />
                  <span className="font-medium text-lg">
                    {order.payment_method === "cash_at_counter"
                      ? "Cash at Counter"
                      : "Card Payment"}
                  </span>
                </div>
              </div>
            </div>

            {/* Vehicle Information - Add this new section for KOT */}
            {order.vehicle_data && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
                  <Car className="h-5 w-5 mr-2" />
                  Vehicle Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="bg-white p-3 rounded-lg border border-blue-100">
                    <p className="text-sm font-medium text-blue-700 mb-1">
                      Make
                    </p>
                    <p className="font-bold text-blue-900">
                      {order.vehicle_data.make}
                    </p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-blue-100">
                    <p className="text-sm font-medium text-blue-700 mb-1">
                      Colour
                    </p>
                    <p className="font-bold text-blue-900">
                      {order.vehicle_data.colour}
                    </p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-blue-100">
                    <p className="text-sm font-medium text-blue-700 mb-1">
                      Registration
                    </p>
                    <p className="font-bold text-blue-900 font-mono text-lg">
                      {order.vehicle_data.registrationNumber}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Order Items */}
            <div className="mb-6">
              <h4 className="font-bold text-lg text-secondary-900 mb-3 border-b border-secondary-200 pb-2">
                Order Items ({order.orderItems?.length || 0})
              </h4>
              <div className="space-y-3">
                {order.orderItems && order.orderItems.length > 0 ? (
                  order.orderItems.map((item, index) => (
                    <div
                      key={item.id}
                      className="bg-secondary-50 p-4 rounded-lg border-l-4 border-green-500 shadow-sm"
                    >
                      <div className="flex flex-col md:flex-row md:justify-between md:items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="bg-green-600 text-white text-sm font-bold px-2 py-1 rounded-full min-w-[24px] text-center">
                              {item.quantity}
                            </span>
                            <h5 className="font-bold text-lg text-secondary-900">
                              {item.menuItem?.name ||
                                `Item #${item.menu_item_id}`}
                            </h5>
                            {item.menuItem?.category_name && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {item.menuItem.category_name}
                              </span>
                            )}
                          </div>
                          {item.menuItem?.description && (
                            <p className="text-sm text-secondary-600 ml-8 mb-2">
                              {item.menuItem.description}
                            </p>
                          )}

                          {/* Display addons in KOT view */}
                          {item.addons && item.addons.length > 0 && (
                            <div className="ml-8 bg-blue-50 border border-blue-200 rounded p-3 mt-2">
                              <p className="text-sm font-bold text-blue-900 mb-2">
                                🍽️ Add-ons:
                              </p>
                              <div className="space-y-1">
                                {item.addons.map((addon, addonIndex) => (
                                  <div
                                    key={`${addon.id}-${addonIndex}`}
                                    className="flex justify-between items-center text-sm"
                                  >
                                    <div className="text-blue-800">
                                      <span className="font-bold">
                                        {addon.quantity}x
                                      </span>
                                      <span className="ml-2">
                                        {addon.menuItemAddon?.name ||
                                          `Addon #${addon.menu_item_addon_id}`}
                                      </span>
                                    </div>
                                    <div className="text-blue-900 font-medium">
                                      £
                                      {(
                                        addon.quantity *
                                        parseFloat(addon.price_at_time)
                                      ).toFixed(2)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {item.special_instructions && (
                            <div className="ml-8 bg-yellow-50 border border-yellow-200 rounded p-2 mt-2">
                              <p className="text-sm text-yellow-800">
                                <strong>Special Instructions:</strong>{" "}
                                {item.special_instructions}
                              </p>
                            </div>
                          )}
                          {item.allergy_information && (
                            <div className="ml-8 bg-red-50 border border-red-200 rounded p-2 mt-2">
                              <p className="text-sm text-red-800">
                                <strong>⚠️ Allergy Info:</strong>{" "}
                                {item.allergy_information}
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="text-left md:text-right ml-8 md:ml-4 mt-2 md:mt-0">
                          <p className="font-bold text-lg text-secondary-900">
                            £
                            {item.total_price
                              ? parseFloat(item.total_price).toFixed(2)
                              : (
                                  item.quantity * parseFloat(item.price)
                                ).toFixed(2)}
                          </p>
                          <p className="text-sm text-secondary-600">
                            £{item.price} base price
                          </p>
                          {item.addons && item.addons.length > 0 && (
                            <p className="text-xs text-green-600 font-medium">
                              + add-ons included
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-secondary-500">
                    No items found for this order
                  </div>
                )}
              </div>
            </div>

            {/* Special Instructions & Allergies */}
            {(order.delivery_instructions || order.allergy_note) && (
              <div className="space-y-3 mb-6">
                {order.delivery_instructions && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">
                      📝 Special Instructions
                    </h4>
                    <p className="text-blue-800">
                      {order.delivery_instructions}
                    </p>
                  </div>
                )}
                {order.allergy_note && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="font-semibold text-red-900 mb-2">
                      ⚠️ Allergy Information
                    </h4>
                    <p className="text-red-800">{order.allergy_note}</p>
                  </div>
                )}
              </div>
            )}

            {/* Order Summary */}
            <div className="bg-secondary-100 p-4 rounded-lg border border-secondary-200">
              <div className="flex justify-between items-center mb-2">
                <span className="font-semibold text-secondary-900">Total:</span>
                <span className="font-bold text-2xl text-secondary-900">
                  £{order.subtotal || 0}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-secondary-600">
                  Payment Status:
                </span>
                <span
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    order.payment_status === "paid"
                      ? "bg-green-100 text-green-800"
                      : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {order.payment_status}
                </span>
              </div>
            </div>

            {/* Order Status */}
            <div className="text-center mt-4">
              <div
                className={`inline-flex items-center px-4 py-2 rounded-full font-medium ${getStatusColor(
                  order.status
                )}`}
              >
                Status: {formatStatusLabel(order.status)}
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="bg-secondary-50 p-4 rounded-b-lg border-t border-secondary-200">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center space-y-3 md:space-y-0">
              <div className="text-sm text-secondary-600 text-center md:text-left">
                Generated: {new Date().toLocaleString()}
              </div>
              <div className="flex flex-wrap justify-center md:justify-end gap-2">
                <button
                  onClick={handleShare}
                  className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Share2 className="h-4 w-4" />
                  <span>Share</span>
                </button>
                <button
                  onClick={handleDownload}
                  className="flex items-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Download className="h-4 w-4" />
                  <span>Download</span>
                </button>
                <button
                  onClick={handlePrint}
                  className="flex items-center space-x-2 px-3 py-2 bg-secondary-600 text-white rounded-lg hover:bg-secondary-700 transition-colors"
                >
                  <Printer className="h-4 w-4" />
                  <span>Print</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="text-center py-12">
        <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Package className="h-8 w-8 text-red-400" />
        </div>
        <h3 className="text-lg font-medium text-secondary-900 mb-2">
          Failed to load orders
        </h3>
        <p className="text-secondary-600 mb-4">
          {error.response?.data?.message ||
            error.message ||
            "An error occurred while loading orders"}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Retry
        </button>
      </div>
    );
  }

  // Handle case when orders is null or undefined
  if (!orders) {
    return (
      <div className="text-center py-12">
        <div className="h-16 w-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Package className="h-8 w-8 text-secondary-400" />
        </div>
        <h3 className="text-lg font-medium text-secondary-900 mb-2">
          No orders data
        </h3>
        <p className="text-secondary-600">Please try refreshing the page</p>
      </div>
    );
  }

  return (
    <div className=" ">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-secondary-900">Orders</h1>
        <p className="text-secondary-600">
          Manage and track incoming customer orders
        </p>
      </div>

      {/* Search and Filters - Tablet Optimized */}
      <div className="bg-white rounded-lg border border-secondary-200 p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 space-y-3 md:space-y-0">
          <h3 className="text-base md:text-lg font-semibold text-secondary-900">
            Search & Filters
          </h3>
          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              className="text-sm text-red-600 hover:text-red-700 underline self-start md:self-auto"
            >
              Clear all filters
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
            <input
              type="text"
              placeholder="Search orders, customers, items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10 w-full text-sm md:text-base"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input-field w-full text-sm md:text-base"
          >
            <option value="">All Status</option>
            <option value="pending">⏳ Pending</option>
            <option value="pending_approval">🔍 Pending Approval</option>
            <option value="confirmed">✅ Confirmed</option>
            <option value="preparing">👨‍🍳 Preparing</option>
            <option value="ready">🎉 Ready</option>
            <option value="out_for_delivery">🚚 Out for Delivery</option>
            <option value="completed">✅ Completed</option>
            <option value="cancelled">❌ Cancelled</option>
          </select>

          <select
            value={orderTypeFilter}
            onChange={(e) => setOrderTypeFilter(e.target.value)}
            className="input-field w-full text-sm md:text-base"
          >
            <option value="">All Order Types</option>
            <option value="dine_in">🍽️ Dine-in</option>
            <option value="takeaway">📦 Takeaway</option>
            <option value="delivery">🚚 Delivery</option>
            <option value="click_and_collect">🏃 Click & Collect</option>
            <option value="scheduled">📅 Scheduled</option>
          </select>

          <select
            value={paymentMethodFilter}
            onChange={(e) => setPaymentMethodFilter(e.target.value)}
            className="input-field w-full text-sm md:text-base"
          >
            <option value="">All Payment Methods</option>
            <option value="stripe">💳 Card Payment</option>
            <option value="cash_at_counter">💵 Cash at Counter</option>
            <option value="apple_pay">🍎 Apple Pay</option>
            <option value="google_pay">🤖 Google Pay</option>
          </select>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
            <select
              value={dateRangeFilter}
              onChange={(e) => setDateRangeFilter(e.target.value)}
              className="input-field max-w-xs text-sm md:text-base"
            >
              <option value="">All Dates</option>
              <option value="today">📅 Today</option>
              <option value="yesterday">📅 Yesterday</option>
              <option value="week">📅 This Week</option>
              <option value="month">📅 This Month</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="input-field max-w-xs text-sm md:text-base"
            >
              <option value="date">📅 Sort by Date</option>
              <option value="total">💰 Sort by Total</option>
              <option value="id">🆔 Sort by Order ID</option>
            </select>

            <button
              onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
              className="px-3 py-2 border border-secondary-200 rounded-lg hover:bg-secondary-50 text-sm md:text-base"
            >
              {sortOrder === "asc" ? "↑ Ascending" : "↓ Descending"}
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <span className="text-sm text-secondary-600">
              📊 {filteredOrders.length} of {orders.length} orders
            </span>
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="flex items-center space-x-2">
        <button
          onClick={() =>
            updateNotificationSettings({ sound: !notificationSettings.sound })
          }
          className={`p-2 rounded-lg transition-colors ${
            notificationSettings.sound
              ? "bg-green-100 text-green-600 hover:bg-green-200"
              : "bg-gray-100 text-gray-400 hover:bg-gray-200"
          }`}
          title={notificationSettings.sound ? "Sound On" : "Sound Off"}
        >
          {notificationSettings.sound ? (
            <Bell className="h-5 w-5" />
          ) : (
            <BellOff className="h-5 w-5" />
          )}
        </button>
        <button
          onClick={() =>
            updateNotificationSettings({
              browser: !notificationSettings.browser,
            })
          }
          className={`p-2 rounded-lg transition-colors ${
            notificationSettings.browser
              ? "bg-blue-100 text-blue-600 hover:bg-blue-200"
              : "bg-gray-100 text-gray-400 hover:bg-gray-200"
          }`}
          title={
            notificationSettings.browser
              ? "Browser Notifications On"
              : "Browser Notifications Off"
          }
        >
          {notificationSettings.browser ? (
            <Smartphone className="h-5 w-5" />
          ) : (
            <Smartphone className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Orders List - Tablet Optimized */}
      <div className="space-y-3 md:space-y-4">
        {filteredOrders.map((order) => {
          const inlineActions = getInlineStatusActions(order);

          return (
            <div
              key={order.id}
              className="card hover:shadow-md transition-all duration-200 hover-lift"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
                <div className="flex items-center space-x-3 md:space-x-4">
                  <div className="h-10 w-10 md:h-12 md:w-12 bg-primary-100 rounded-full flex items-center justify-center">
                    <Package className="h-5 w-5 md:h-6 md:w-6 text-primary-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 mb-1">
                      <h3 className="font-semibold text-secondary-900 text-sm md:text-base">
                        Order #{getDisplayOrderId(order)}
                      </h3>
                      {(() => {
                        const kotReference =
                          order.kot_reference ||
                          order.kotReference ||
                          order.kot?.reference;
                        return (
                          kotReference && (
                            <span className="font-mono text-xs md:text-sm font-bold text-green-600 bg-green-50 border border-green-200 px-2 py-1 rounded flex items-center space-x-1">
                              <span>📋</span>
                              <span>KOT: {kotReference}</span>
                            </span>
                          )
                        );
                      })()}
                    </div>
                    <p className="text-xs md:text-sm text-secondary-600">
                      User #{order.user_id} • {order.orderItems?.length || 0}{" "}
                      items • {order.order_type}
                    </p>
                    <p className="text-xs text-secondary-500">
                      📅 {new Date(order.created_at).toLocaleString()}
                    </p>
                    {order.orderItems && order.orderItems.length > 0 && (
                      <p className="text-xs text-secondary-500 max-w-full md:max-w-xs truncate">
                        {order.orderItems
                          .slice(0, 2)
                          .map((item) => {
                            const itemName =
                              item.menuItem?.name ||
                              `Item #${item.menu_item_id}`;
                            const hasAddons =
                              item.addons && item.addons.length > 0;
                            return hasAddons
                              ? `${itemName} (+addons)`
                              : itemName;
                          })
                          .join(", ")}
                        {order.orderItems.length > 2 &&
                          ` +${order.orderItems.length - 2} more`}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                  <div className="text-left sm:text-right">
                    <p className="font-bold text-secondary-900 text-sm md:text-base">
                      💰 £{order.subtotal || 0}
                    </p>
                    <span
                      className={`inline-flex items-center px-2 py-1 md:px-2.5 md:py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                        order.status
                      )}`}
                    >
                      {(() => {
                        const flow = getStatusFlow(order);
                        const currentStatus = flow.flow.find(
                          (s) => s.status === order.status
                        );
                        return currentStatus
                          ? `${currentStatus.icon} ${currentStatus.label}`
                          : formatStatusLabel(order.status);
                      })()}
                    </span>
                    <p className="text-xs text-secondary-500 mt-1">
                      {order.payment_method === "cash_at_counter"
                        ? "💵 Cash"
                        : "💳 Card"}
                    </p>
                  </div>

                  {/* Enhanced Inline Actions Section */}
                  <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:space-x-3">
                    {/* Status Actions */}
                    {inlineActions.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {inlineActions.map((action, index) => (
                          <button
                            key={index}
                            onClick={
                              action.disabled ? undefined : action.action
                            }
                            disabled={action.loading || action.disabled}
                            className={`
                              group relative flex items-center justify-center space-x-1.5 
                              px-3 py-2 text-white rounded-lg text-xs font-semibold 
                              ${action.color} disabled:opacity-50 
                              transition-all duration-200 hover:shadow-md hover:scale-105
                              active:scale-95 border border-transparent hover:border-white/20
                              min-w-[60px] sm:min-w-[80px]
                              ${action.disabled ? "cursor-not-allowed" : ""}
                            `}
                            title={
                              action.disabled
                                ? "This action is only available to admin"
                                : action.label
                            }
                          >
                            {action.loading ? (
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                            ) : (
                              <>
                                <span className="text-sm">{action.icon}</span>
                                <span className="hidden sm:inline font-medium">
                                  {action.label}
                                </span>
                              </>
                            )}
                            {/* Hover tooltip for mobile */}
                            <div
                              className="absolute -top-8 left-1/2 transform -translate-x-1/2 
                                          bg-gray-900 text-white text-xs px-2 py-1 rounded 
                                          opacity-0 group-hover:opacity-100 transition-opacity
                                          pointer-events-none whitespace-nowrap z-10 sm:hidden"
                            >
                              {action.disabled ? "Admin Only" : action.label}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex space-x-2 self-start sm:self-auto">
                    <button
                      onClick={() => setSelectedOrder(order)}
                      className="p-2 text-secondary-400 hover:text-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                      title="View Order Details"
                    >
                      <Eye className="h-4 w-4 md:h-5 md:w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredOrders.length === 0 && (
        <div className="text-center py-12">
          <div className="h-16 w-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Package className="h-8 w-8 text-secondary-400" />
          </div>
          <h3 className="text-lg font-medium text-secondary-900 mb-2">
            {searchTerm || statusFilter ? "No orders found" : "No orders yet"}
          </h3>
          <p className="text-secondary-600">
            {searchTerm || statusFilter
              ? "Try adjusting your search terms or filters"
              : "Orders will appear here when customers place them"}
          </p>
        </div>
      )}

      {/* Order Modal */}
      {selectedOrder && (
        <OrderModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          setKotOrder={setKotOrder}
          setShowKotView={setShowKotView}
          getStatusColor={getStatusColor}
          getStatusFlow={getStatusFlow}
          getStatusAction={getStatusAction}
          updateOrderStatusMutation={updateOrderStatusMutation}
        />
      )}

      {/* KOT Modal */}
      {showKotView && kotOrder && (
        <KotModal
          order={kotOrder}
          onClose={() => {
            setShowKotView(false);
            setKotOrder(null);
          }}
        />
      )}
    </div>
  );
};

export default Orders;
